import React from 'react';

import { TableView } from '~components/table-view';

import buildColumns from '~support-at-home/forms/budgets/bulkCopyPreviewList';

import { BudgetPreviewRow } from '~support-at-home/actions/budgets';

import { buildSampleTableRows } from './sampleTableRows';

interface ComponentProps {
  /** Head slice then tail slice, in selection order, exactly as the API sends them. */
  rows: BudgetPreviewRow[];
  firstCount: number;
  lastCount: number;
  /** Full selection size (or full copied count), independent of how many rows were actually sampled. */
  total: number;
  /**
   * How many ids the sample actually covers, when that differs from `firstCount + lastCount` (TAV-3258).
   * `firstCount`/`lastCount` describe only `rows`' own population (e.g. Preview's copyable subset), which
   * can undercount the assessed sample when `rows` is a filtered view of a larger sampled set. Defaults to
   * `firstCount + lastCount` so callers with no such gap (Review, and FailureList's own instances) are
   * unaffected.
   */
  assessed?: number;
  /**
   * Adds the Services Moved column. Set when the run has services changing contribution category -
   * detection, not the provider's choice, so the column stays when they switch the move off.
   */
  showMoved?: boolean;
  /** Distinguishes the preview and review instances: the table id and data-cy hooks. */
  idPrefix: string;
  /** Overrides the default "showing first/last" caption. Only shown when a break is present. */
  caption?: React.ReactNode;
}

/**
 * The copied/will-be-copied budget table shared by the preview (step 3) and review (step 4) steps of the
 * bulk-copy wizard (TAV-3258). Renders the server-provided head-and-tail sample with a break row spliced in
 * between when the sample does not cover the whole list, following the `ExportDialog.tsx` precedent.
 *
 * Renders nothing when there are no rows, so both callers can drop it in unconditionally.
 */
const BudgetSampleList: React.FC<ComponentProps> = ({
  rows,
  firstCount,
  lastCount,
  total,
  assessed,
  showMoved = false,
  idPrefix,
  caption,
}) => {
  if (rows.length === 0) return null;

  // Resolved once so the table (via buildSampleTableRows) and the caption below can never disagree about
  // whether a break belongs between them.
  const sampleCovered = assessed ?? firstCount + lastCount;

  const tableRows = buildSampleTableRows(
    rows.map((row) => ({ ...row, id: row.budget_id })),
    firstCount,
    lastCount,
    total,
    sampleCovered,
  );
  const hasBreak = total > sampleCovered;

  return (
    <div>
      {/* Height-capped + scrollable: see .bulk-copy-scroll-table in _claims.scss */}
      <div className="bulk-copy-scroll-table">
        <TableView id={`${idPrefix}-table`} data={tableRows} columns={buildColumns(showMoved)} readonly striped />
      </div>
      {hasBreak && (
        <p className="text-muted small mt-1 mb-0">
          {caption ?? `Showing the first ${firstCount} and last ${lastCount} of ${total} budgets.`}
        </p>
      )}
    </div>
  );
};

export default BudgetSampleList;
